import React from "react";

const GROUPS = [
  { key: "anaerobe", label: "Anaerobes", hint: "beneficial commensals", color: "#2A3B2C" },
  { key: "aerobe", label: "Aerobes", color: "#D49A44" },
  { key: "yeast", label: "Yeasts", color: "#C05C42" },
];

const DIVERSITY_TONE = {
  Rich: { text: "text-[#2A3B2C]", bg: "bg-[#2A3B2C]/10" },
  Good: { text: "text-[#2A3B2C]", bg: "bg-[#2A3B2C]/10" },
  Moderate: { text: "text-[#8a6620]", bg: "bg-[#D49A44]/15" },
  Low: { text: "text-[#8F3A3A]", bg: "bg-[#8F3A3A]/10" },
};

export const MicrobiomeSummary = ({ summary, diversityNote, date }) => {
  if (!summary) return null;
  const tone = DIVERSITY_TONE[summary.diversity_label] || DIVERSITY_TONE.Moderate;
  const groups = summary.group_counts || {};
  const totalGroup = (groups.anaerobe || 0) + (groups.aerobe || 0) + (groups.yeast || 0) || 1;

  return (
    <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-6" data-testid="microbiome-summary">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="label-eyebrow">Gut snapshot{date ? ` · ${new Date(date).toLocaleDateString()}` : ""}</p>
          <div className="flex items-end gap-3 mt-2">
            <span className={`font-serif text-4xl ${tone.text}`} data-testid="diversity-label">{summary.diversity_label}</span>
            <span className="text-sm text-[#5C6359] mb-1">diversity · {summary.species_detected} species detected</span>
          </div>
        </div>
        {summary.attention_count > 0 && (
          <span className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full bg-[#8F3A3A]/10 text-[#8F3A3A]" data-testid="microbiome-attention">
            {summary.attention_count} out of range
          </span>
        )}
      </div>

      <div className="mt-5">
        <p className="text-[10px] uppercase tracking-widest text-[#5C6359] mb-2">Group balance</p>
        <div className="flex h-3 rounded-full overflow-hidden bg-[#E5DEC3]">
          {GROUPS.map((g) => {
            const pct = ((groups[g.key] || 0) / totalGroup) * 100;
            if (pct === 0) return null;
            return <div key={g.key} style={{ width: `${pct}%`, background: g.color }} title={`${g.label}: ${groups[g.key]}`} />;
          })}
        </div>
        <div className="flex flex-wrap gap-4 mt-2 text-xs text-[#5C6359]">
          {GROUPS.map((g) => (
            <span key={g.key} className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: g.color }} />
              {g.label} · {groups[g.key] || 0}{g.hint ? ` (${g.hint})` : ""}
            </span>
          ))}
        </div>
      </div>

      {diversityNote && (
        <p className="mt-5 text-sm text-[#5C6359] italic border-l-2 border-[#D49A44] pl-3">{diversityNote}</p>
      )}
      <p className="mt-4 text-[10px] text-[#9A8F7E]">Diversity is a derived proxy from the count of cultured commensal species — read alongside your practitioner&apos;s interpretation.</p>
    </div>
  );
};
